import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import new components
import BookingPage from './components/BookingPage';
import BookingsPage from './components/BookingsPage';
import AdminPanel from './components/AdminPanel';
import TeeSheetView from './components/TeeSheetView';
import PresentationTeeSheet from './components/PresentationTeeSheet';
import PaymentModal from './components/PaymentModal';

// UI Components (using shadcn imports)
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './components/ui/dialog';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Calendar } from './components/ui/calendar';
import { useToast } from './hooks/use-toast';
import { Eye, EyeOff, Search, ShoppingCart, Trash2 } from 'lucide-react';
import OTPInput from './components/OTPInput';
import BiometricAuth from './components/BiometricAuth';

// Get backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

// Dynamic domain detection for API calls
const getCurrentDomain = () => {
  const currentHost = window.location.origin;
  
  // If accessing via custom domain, use same domain for API
  if (currentHost === 'https://bookings.duckhookgolfers.co.za') {
    return 'https://bookings.duckhookgolfers.co.za/api';
  }
  
  // If accessing via emergent.host domain, use same domain for API
  if (currentHost.includes('.emergent.host')) {
    return `${currentHost}/api`;
  }
  
  // Fallback to configured API
  return API;
};

// Configure axios defaults with dynamic domain
const dynamicAPI = getCurrentDomain();
console.log('Configuring axios with baseURL:', dynamicAPI);
console.log('Current domain:', window.location.origin);
axios.defaults.baseURL = dynamicAPI;

// Ensure axios configuration is properly set
if (!axios.defaults.baseURL) {
  console.error('Axios baseURL not set! Using fallback.');
  axios.defaults.baseURL = `${window.location.origin}/api`;
}

// PWA Install Header Button Component
// const PWAInstallButton = () => {
//   const [deferredPrompt, setDeferredPrompt] = useState(null);
//   const [isExpanded, setIsExpanded] = useState(false);
//   const [isInstallable, setIsInstallable] = useState(false);

//   useEffect(() => {
//     // Register service worker
//     if ('serviceWorker' in navigator) {
//       navigator.serviceWorker.register('/sw.js')
//         .catch((error) => console.warn('Service Worker registration failed:', error));
//     }

//     // Check if app is already installed
//     const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
//     const isInWebAppiOS = (window.navigator.standalone === true);
//     const isInstalled = isStandalone || isInWebAppiOS;

//     // Listen for beforeinstallprompt event
//     const handleBeforeInstallPrompt = (e) => {
//       e.preventDefault();
//       setDeferredPrompt(e);
//       setIsInstallable(true);
//     };

//     // Listen for appinstalled event
//     const handleAppInstalled = () => {
//       setIsExpanded(false);
//       setDeferredPrompt(null);
//     };

//     window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
//     window.addEventListener('appinstalled', handleAppInstalled);

//     return () => {
//       window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
//       window.removeEventListener('appinstalled', handleAppInstalled);
//     };
  // }, []);

  // Close expanded view when user navigates
//   useEffect(() => {
//     const handleLocationChange = () => {
//       setIsExpanded(false);
//     };

//     // Listen for route changes
//     window.addEventListener('popstate', handleLocationChange);
    
//     return () => {
//       window.removeEventListener('popstate', handleLocationChange);
//     };
//   }, []);

//   const handleInstallClick = async () => {
//     try {
//       if (deferredPrompt) {
//         deferredPrompt.prompt();
//         const { outcome } = await deferredPrompt.userChoice;
        
//         if (outcome === 'accepted') {
//           setIsExpanded(false);
//           localStorage.setItem('pwa-install-dismissed', Date.now().toString());
//         }
        
//         setDeferredPrompt(null);
//       }
//     } catch (error) {
//       console.error('PWA Install error:', error);
//     }
//   };

//   const handleDismiss = () => {
//     setIsExpanded(false);
//     localStorage.setItem('pwa-install-dismissed', Date.now().toString());
//   };

//   // Check if we should show the install button
//   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
//   const isIOSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
//   const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
//   const isInWebAppiOS = (window.navigator.standalone === true);
//   const isInstalled = isStandalone || isInWebAppiOS;
  
//   // Don't show if recently dismissed (within 24 hours)
//   const lastDismissed = localStorage.getItem('pwa-install-dismissed');
//   const recentlyDismissed = lastDismissed && (Date.now() - parseInt(lastDismissed) < 24 * 60 * 60 * 1000);
  
//   // Show if: mobile device, not installed, not recently dismissed, and (installable OR iOS Safari)
//   const shouldShow = isMobile && !isInstalled && !recentlyDismissed && (isInstallable || isIOSafari);
  
//   if (!shouldShow) return null;

//   return (
//     <div className="relative">
//       {/* Install App Button */}
//       <button
//         onClick={() => setIsExpanded(!isExpanded)}
//         className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors"
//         type="button"
//       >
//         <span className="text-lg">📱</span>
//         <span className="hidden sm:inline">Install App</span>
//         <span className="sm:hidden">Install</span>
//         <svg 
//           className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
//           fill="none" 
//           stroke="currentColor" 
//           viewBox="0 0 24 24"
//         >
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
//         </svg>
//       </button>

//       {/* Expanded Install Options */}
//       {isExpanded && (
//         <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
//           <div className="flex items-start justify-between mb-3">
//             <div className="flex items-center gap-2">
//               <span className="text-2xl">📱</span>
//               <h3 className="font-semibold text-gray-900">Install DuckHook Golfers</h3>
//             </div>
//             <button 
//               onClick={handleDismiss}
//               className="text-gray-400 hover:text-gray-600 p-1"
//               type="button"
//             >
//               ✕
//             </button>
//           </div>
          
//           {isIOSafari ? (
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">
//                 To install this app on your iPhone/iPad:
//               </p>
//               <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
//                 <li>Tap the <strong>Share</strong> button (⬆️) at the bottom of Safari</li>
//                 <li>Select <strong>"Add to Home Screen"</strong> from the menu</li>
//                 <li>Tap <strong>"Add"</strong> to confirm</li>
//               </ol>
//               <div className="flex gap-2 mt-4">
//                 <button 
//                   onClick={handleDismiss}
//                   className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors"
//                   type="button"
//                 >
//                   Got It
//                 </button>
//                 <button 
//                   onClick={handleDismiss}
//                   className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm transition-colors"
//                   type="button"
//                 >
//                   Later
//                 </button>
//               </div>
//             </div>
//           ) : (
//             <div className="space-y-3">
//               <p className="text-sm text-gray-600">
//                 Get quick access to book tee times with our mobile app!
//               </p>
//               <div className="flex gap-2">
//                 {deferredPrompt ? (
//                   <button 
//                     onClick={handleInstallClick}
//                     className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors"
//                     type="button"
//                   >
//                     Install Now
//                   </button>
//                 ) : (
//                   <button 
//                     onClick={handleDismiss}
//                     className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors"
//                     type="button"
//                   >
//                     Got It
//                   </button>
//                 )}
//                 <button 
//                   onClick={handleDismiss}
//                   className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm transition-colors"
//                   type="button"
//                 >
//                   Later
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Initialize token from localStorage on mount
  useEffect(() => {
    console.log('🔍 useEffect[mount]: Initializing auth from localStorage...');
    const savedToken = localStorage.getItem('token');
    console.log('🔍 useEffect[mount]: Found token in localStorage:', savedToken ? `${savedToken.substring(0, 10)}...` : 'null');
    if (savedToken) {
      setToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔍 useEffect[token]: Token changed, value:', token ? `${token.substring(0, 10)}...` : 'null');
    if (token) {
      console.log('🔍 useEffect[token]: Setting axios Authorization header and calling fetchUser...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      console.log('🔍 useEffect[token]: No token found, clearing Authorization header');
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      console.log('🔍 fetchUser: Making request to /auth/me...');
      console.log('🔍 fetchUser: Current token in localStorage:', localStorage.getItem('token')?.substring(0, 20) + '...');
      console.log('🔍 fetchUser: Current axios auth header:', axios.defaults.headers.common['Authorization']?.substring(0, 30) + '...');
      
      const response = await axios.get('/auth/me');
      console.log('🔍 fetchUser: Response received:', response.data);
      console.log('🔍 fetchUser: User ID:', response.data?.id);
      console.log('🔍 fetchUser: User Name:', response.data?.first_name, response.data?.last_name);
      console.log('🔍 fetchUser: User Email:', response.data?.email);
      console.log('🔍 fetchUser: User role:', response.data?.role);
      
      setUser(response.data);
      setLoading(false);
      console.log('🔍 fetchUser: User state updated successfully');
    } catch (error) {
      console.error('🚨 Error fetching user:', error);
      console.error('🚨 Error response:', error.response?.data);
      console.error('🚨 Error status:', error.response?.status);
      logout();
    }
  };

  const login = (token, userData, rememberMe = false) => {
    // Always store in localStorage for now - session handling can be added later
    console.log('🔍 login: Token received, length:', token ? token.length : 'null');
    console.log('🔍 login: UserData received:', userData);
    console.log('🔍 login: User role from login:', userData?.role);
    
    localStorage.setItem('token', token);
    localStorage.setItem('lastActivity', Date.now().toString());
    
    if (rememberMe) {
      // Store additional data for biometric auth
      localStorage.setItem('rememberMe', 'true');
      localStorage.setItem('userEmail', userData.email);
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('userEmail');
    }
    setToken(token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('🔍 login: Auth header set, user state updated');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('lastActivity');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Activity tracking and token refresh for inactivity timeout
  useEffect(() => {
    if (!token) return;

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
    const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // Refresh token every 10 minutes
    const isRemembered = localStorage.getItem('rememberMe') === 'true';

    // Update last activity on user interaction
    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Check for inactivity and handle token refresh
    const checkActivity = async () => {
      const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;

      // Only enforce inactivity timeout for non-remembered sessions
      if (!isRemembered && timeSinceActivity > INACTIVITY_TIMEOUT) {
        console.log('🔒 Session expired due to inactivity');
        logout();
        window.location.href = '/?session=expired';
        return;
      }

      // Refresh token if user is active (for non-remembered sessions)
      if (!isRemembered && timeSinceActivity < INACTIVITY_TIMEOUT) {
        try {
          const response = await axios.post('/auth/refresh-token');
          if (response.data.access_token) {
            localStorage.setItem('token', response.data.access_token);
            setToken(response.data.access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
            console.log('🔄 Token refreshed successfully');
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Only logout if it's an auth error
          if (error.response?.status === 401) {
            logout();
            window.location.href = '/?session=expired';
          }
        }
      }
    };

    // Add event listeners for user activity
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Set initial activity
    updateActivity();

    // Check activity and refresh token periodically
    const intervalId = setInterval(checkActivity, TOKEN_REFRESH_INTERVAL);

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      isAuthenticated: !!token,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Cart Context for managing booking cart
const CartContext = createContext();

const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('booking_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('booking_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart
  const addToCart = (item) => {
    // Check if same slot already in cart
    const slotExists = cartItems.some(
      ci => ci.event_id === item.event_id && 
           ci.tee_time === item.tee_time && 
           ci.tee_name === item.tee_name &&
           ci.slot_index === item.slot_index
    );
    
    if (slotExists) {
      return { success: false, message: 'This slot is already in your cart' };
    }
    
    // Check if user already has an item from the same event in cart
    // (they can only book once per event)
    const eventExists = cartItems.some(ci => ci.event_id === item.event_id);
    
    if (eventExists) {
      return { success: false, message: 'You already have a booking for this event in your cart. You can only book once per event.' };
    }
    
    const cartItem = {
      ...item,
      cart_item_id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      added_at: new Date().toISOString()
    };
    
    setCartItems(prev => [...prev, cartItem]);
    return { success: true, message: 'Added to cart' };
  };

  // Remove item from cart
  const removeFromCart = (cartItemId) => {
    setCartItems(prev => prev.filter(item => item.cart_item_id !== cartItemId));
  };

  // Clear entire cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Calculate cart totals
  const getCartTotals = () => {
    let greenFeeTotal = 0;
    let cartFeeTotal = 0;
    let mhalaFeeTotal = 0;
    let dinnerFeeTotal = 0;

    cartItems.forEach(item => {
      greenFeeTotal += item.green_fee || 0;
      cartFeeTotal += item.cart_fee || 0;
      if (item.mhala_hole_in_one) {
        mhalaFeeTotal += item.mhala_fee || 50;
      }
      if (item.dinner_attending) {
        dinnerFeeTotal += item.dinner_fee || 0;
      }
    });

    return {
      greenFeeTotal,
      cartFeeTotal,
      mhalaFeeTotal,
      dinnerFeeTotal,
      grandTotal: greenFeeTotal + cartFeeTotal + mhalaFeeTotal + dinnerFeeTotal,
      itemCount: cartItems.length
    };
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      getCartTotals,
      itemCount: cartItems.length
    }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Header Component
const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [menuValue, setMenuValue] = React.useState('');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMenuChange = (value) => {
    if (value === 'dashboard') navigate('/dashboard');
    else if (value === 'profile') navigate('/profile');
    else if (value === 'bookings') navigate('/bookings');
    else if (value === 'admin') navigate('/admin');
    
    // Reset menu value after navigation to allow re-selection
    setTimeout(() => setMenuValue(''), 100);
  };

  return (
    <header className="bg-gradient-to-r from-red-600 to-gray-800 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo and Branding */}
          <div 
            className="flex items-center space-x-3 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
          >
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_1e54c772-d700-4589-871e-546ca3d3939b/artifacts/3kky31bc_Logo.png" 
                alt="DuckHook Golfers Logo"
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  // Fallback if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'inline';
                }}
              />
              <span className="text-lg font-bold text-red-600" style={{ display: 'none' }}>DH</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Duckhook Golfers</h1>
            </div>
          </div>

          {/* Sponsor Logo and User Actions */}
          <div className="flex items-center space-x-4">
            {/* Sponsor Logo - Srixon */}
            <div className="hidden md:flex items-center space-x-2 pr-4 border-r border-white/30">
              <span className="text-xs text-white/70">Sponsored by</span>
              <img 
                src="/srixon-black-logo.png" 
                alt="Srixon"
                className="h-6 opacity-90 hover:opacity-100 transition-opacity"
                onError={(e) => {
                  // Fallback to red logo if black fails
                  e.target.src = "/srixon-red-logo.jpg";
                }}
              />
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                {/* User Info */}
                <div className="hidden sm:flex items-center space-x-2">
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-emerald-100">
                      {user?.membership_tier && user.membership_tier !== 'non-member' 
                        ? `${user.membership_tier.charAt(0).toUpperCase() + user.membership_tier.slice(1)} Member`
                        : 'Non-Member'
                      }
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold">
                      {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </span>
                  </div>
                </div>

                {/* PWA Install Button */}
                {/* <PWAInstallButton /> */}

                {/* Cart Button */}
                {itemCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative text-white hover:bg-emerald-500"
                    onClick={() => navigate('/cart')}
                    data-testid="cart-button"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    <span className="absolute -top-1 -right-1 bg-yellow-400 text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {itemCount}
                    </span>
                  </Button>
                )}

                {/* Quick Actions Menu */}
                <div className="relative">
                  <Select value={menuValue} onValueChange={handleMenuChange}>
                    <SelectTrigger className="w-[140px] bg-emerald-500 border-emerald-400 text-white hover:bg-emerald-400">
                      <SelectValue placeholder="Menu" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dashboard">
                        🏠 Dashboard
                      </SelectItem>
                      <SelectItem value="profile">
                        👤 Profile
                      </SelectItem>
                      <SelectItem value="bookings">
                        📅 My Bookings
                      </SelectItem>
                      {user?.role === 'admin' && (
                        <SelectItem value="admin">
                          ⚙️ Admin Panel
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-white text-white hover:bg-white hover:text-emerald-600"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-white hover:bg-emerald-500"
                  onClick={() => navigate('/login')}
                  data-testid="login-nav-btn"
                >
                  Login
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-white text-white hover:bg-white hover:text-emerald-600"
                  onClick={() => navigate('/register')}
                  data-testid="register-nav-btn"
                >
                  Register
                </Button>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Landing Page Component - Redesigned according to requirements
const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();  // Added login from useAuth
  const [showLogin, setShowLogin] = useState(false);
  const { toast } = useToast();

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    totpToken: ''
  });
  const [showMFA, setShowMFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // OTP login states for landing page
  const [useOTPLanding, setUseOTPLanding] = useState(false);
  const [otpStepLanding, setOtpStepLanding] = useState('email'); // 'email' or 'verify'
  const [otpEmailLanding, setOtpEmailLanding] = useState('');
  const [otpCodeLanding, setOtpCodeLanding] = useState('');
  const [otpLoadingLanding, setOtpLoadingLanding] = useState(false);
  
  // Remember me functionality for landing page
  const [rememberMeLanding, setRememberMeLanding] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setError('');

    try {
      await axios.post('/auth/forgot-password', {
        email: forgotPasswordEmail
      });
      
      toast({
        title: "Password Reset Email Sent",
        description: "If the email exists, a password reset link has been sent. Please check your email."
      });
      
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset email';
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleLogin: Starting login process...');
    console.log('🔍 handleLogin: Login data:', { email: loginData.email, hasPassword: !!loginData.password, rememberMe: rememberMeLanding });
    
    try {
      const response = await axios.post('/auth/login', {
        username: loginData.email, // Use email as username as per requirements
        password: loginData.password,
        totp_token: loginData.totpToken || undefined,
        remember_me: rememberMeLanding
      });

      console.log('🔍 handleLogin: Login API response received');
      console.log('🔍 handleLogin: Response data:', response.data);
      console.log('🔍 handleLogin: MFA required:', response.data.mfa_required);
      console.log('🔍 handleLogin: Access token received:', !!response.data.access_token);
      console.log('🔍 handleLogin: User data received:', response.data.user);

      if (response.data.mfa_required) {
        console.log('🔍 handleLogin: Redirecting to MFA');
        setShowMFA(true);
        toast({
          title: "MFA Required",
          description: "Please enter your authenticator code to continue.",
        });
      } else {
        console.log('🔍 handleLogin: Calling login function...');
        login(response.data.access_token, response.data.user, rememberMeLanding);
        toast({
          title: "Login Successful",
          description: "Welcome back to Duckhook Golfers!",
        });
        console.log('🔍 handleLogin: Navigating to dashboard...');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('🚨 handleLogin: Login error:', error);
      console.error('🚨 handleLogin: Error response:', error.response?.data);
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
      console.log('🔍 handleLogin: Login process completed');
    }
  };

  const handleRequestOTPLanding = async (e) => {
    e.preventDefault();
    setOtpLoadingLanding(true);
    setError('');

    try {
      await axios.post('/auth/request-otp', {
        email: otpEmailLanding
      });
      
      setOtpStepLanding('verify');
      toast({
        title: "OTP Sent",
        description: "A 6-digit code has been sent to your email address.",
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoadingLanding(false);
    }
  };

  const handleVerifyOTPLanding = async (e) => {
    e.preventDefault();
    setOtpLoadingLanding(true);
    setError('');

    try {
      const response = await axios.post('/auth/verify-otp', {
        email: otpEmailLanding,
        otp_code: otpCodeLanding
      });

      login(response.data.access_token, response.data.user, false);
      toast({
        title: "Login Successful", 
        description: "Welcome back to Duckhook Golfers!",
      });
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setOtpLoadingLanding(false);
    }
  };

  const handleOTPCompleteLanding = (code) => {
    setOtpCodeLanding(code);
  };

  const resetOTPFlowLanding = () => {
    setUseOTPLanding(false);
    setOtpStepLanding('email');
    setOtpEmailLanding('');
    setOtpCodeLanding('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-gray-50">
      {/* Hero Section with Welcome Message */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Welcome Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_golfers-platform/artifacts/mse6hv1k_NEW%20DUCKHOOK%20BANNER%20V1.png" 
                  alt="DuckHook Golfers Main Logo"
                  className="h-80 mx-auto lg:mx-0 mb-6"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Welcome to
                <span className="block">Duckhook Golfers</span>
                <span className="block text-3xl md:text-4xl bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">
                  Event Bookings
                </span>
              </h1>
              
              {/* Sponsor Logos */}
              <div className="flex flex-col items-center lg:items-start gap-3 mt-8 mb-4">
                {/* Srixon Sponsor */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Proudly sponsored by</span>
                  <img 
                    src="/srixon-black-logo.png" 
                    alt="Srixon"
                    className="h-8 opacity-90 hover:opacity-100 transition-opacity"
                    onError={(e) => {
                      e.target.src = "/srixon-red-logo.jpg";
                    }}
                  />
                </div>
                
                {/* ScoreCapture Powered By */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Powered by</span>
                  <img 
                    src="/scorecapture-logo.png" 
                    alt="ScoreCapture"
                    className="h-32 opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
            </div>

            {/* Login Block */}
            <div className="flex-1 max-w-md w-full">
              <Card className="shadow-2xl border-0">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold text-gray-900">Member Login</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Login Method Toggle */}
                  <div className="mb-4">
                    <div className="flex rounded-lg border border-gray-200 p-1">
                      <button
                        type="button"
                        onClick={() => { resetOTPFlowLanding(); setUseOTPLanding(false); }}
                        className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${
                          !useOTPLanding 
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        data-testid="password-login-tab-landing"
                      >
                        Password
                      </button>
                      <button
                        type="button"
                        onClick={() => { resetOTPFlowLanding(); setUseOTPLanding(true); }}
                        className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${
                          useOTPLanding
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        data-testid="otp-login-tab-landing"
                      >
                        OTP
                      </button>
                    </div>
                  </div>

                  {/* Regular Password Login */}
                  {!useOTPLanding && (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="email-landing" className="text-gray-700 font-medium">Email Address</Label>
                        <Input
                          id="email-landing"
                          type="email"
                          value={loginData.email}
                          onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                          required
                          className="mt-1"
                          placeholder="Enter your email"
                          data-testid="login-email-input-landing"
                        />
                      </div>

                      <div>
                        <Label htmlFor="password-landing" className="text-gray-700 font-medium">Password</Label>
                        <div className="relative mt-1">
                          <Input
                            id="password-landing"
                            type={showPassword ? "text" : "password"}
                            value={loginData.password}
                            onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                            required
                            className="pr-10"
                            placeholder="Enter your password"
                            data-testid="login-password-input-landing"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      {showMFA && (
                        <div>
                          <Label htmlFor="totpToken-landing" className="text-gray-700 font-medium">Authenticator Code</Label>
                          <Input
                            id="totpToken-landing"
                            type="text"
                            value={loginData.totpToken}
                            onChange={(e) => setLoginData({...loginData, totpToken: e.target.value})}
                            required={showMFA}
                            className="mt-1"
                            placeholder="Enter 6-digit code"
                            data-testid="mfa-token-input-landing"
                          />
                        </div>
                      )}

                      {error && (
                        <Alert className="border-red-200 bg-red-50">
                          <AlertDescription className="text-red-800">{error}</AlertDescription>
                        </Alert>
                      )}

                      {/* Remember Me Checkbox */}
                      <div className="flex items-center">
                        <input
                          id="remember-me-landing"
                          type="checkbox"
                          checked={rememberMeLanding}
                          onChange={(e) => setRememberMeLanding(e.target.checked)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                        <Label htmlFor="remember-me-landing" className="ml-2 text-sm text-gray-700">
                          Remember me
                        </Label>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                        disabled={loading}
                        data-testid="login-submit-btn-landing"
                      >
                        {loading ? 'Signing In...' : 'Sign In'}
                      </Button>

                      <div className="text-center space-y-2">
                        <button 
                          type="button"
                          className="text-emerald-600 hover:text-emerald-700 text-sm underline"
                          onClick={() => setShowForgotPassword(true)}
                          data-testid="forgot-password-btn-landing"
                        >
                          Forgot Password?
                        </button>
                        <div>
                          <span className="text-gray-600 text-sm">Don't have an account? </span>
                          <button 
                            type="button"
                            onClick={() => navigate('/register')}
                            className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm"
                            data-testid="goto-register-btn-landing"
                          >
                            Sign up
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* OTP Login */}
                  {useOTPLanding && (
                    <div className="space-y-4">
                      {otpStepLanding === 'email' && (
                        <form onSubmit={handleRequestOTPLanding} className="space-y-4">
                          <div>
                            <Label htmlFor="otp-email-landing" className="text-gray-700 font-medium">Email Address</Label>
                            <Input
                              id="otp-email-landing"
                              type="email"
                              value={otpEmailLanding}
                              onChange={(e) => setOtpEmailLanding(e.target.value)}
                              required
                              className="mt-1"
                              placeholder="Enter your email address"
                              data-testid="otp-email-input-landing"
                            />
                          </div>

                          {error && (
                            <Alert className="border-red-200 bg-red-50">
                              <AlertDescription className="text-red-800">{error}</AlertDescription>
                            </Alert>
                          )}

                          <Button 
                            type="submit" 
                            className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                            disabled={otpLoadingLanding}
                            data-testid="request-otp-btn-landing"
                          >
                            {otpLoadingLanding ? 'Sending OTP...' : 'Send OTP'}
                          </Button>
                        </form>
                      )}

                      {otpStepLanding === 'verify' && (
                        <form onSubmit={handleVerifyOTPLanding} className="space-y-4">
                          <div className="text-center mb-3">
                            <p className="text-xs text-gray-600">
                              Code sent to <span className="font-semibold">{otpEmailLanding}</span>
                            </p>
                          </div>

                          <div>
                            <Label className="text-gray-700 font-medium">Verification Code</Label>
                            <div className="mt-2">
                              <OTPInput
                                length={6}
                                onComplete={handleOTPCompleteLanding}
                                disabled={otpLoadingLanding}
                                className="justify-center"
                              />
                            </div>
                          </div>

                          {error && (
                            <Alert className="border-red-200 bg-red-50">
                              <AlertDescription className="text-red-800">{error}</AlertDescription>
                            </Alert>
                          )}

                          <div className="space-y-2">
                            <Button 
                              type="submit" 
                              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                              disabled={otpLoadingLanding || otpCodeLanding.length !== 6}
                              data-testid="verify-otp-btn-landing"
                            >
                              {otpLoadingLanding ? 'Verifying...' : 'Verify & Login'}
                            </Button>

                            <div className="flex justify-center space-x-3 text-xs">
                              <button
                                type="button"
                                onClick={() => setOtpStepLanding('email')}
                                className="text-gray-600 hover:text-gray-800 underline"
                                data-testid="back-to-email-btn-landing"
                              >
                                Change Email
                              </button>
                              <button
                                type="button"
                                onClick={handleRequestOTPLanding}
                                className="text-emerald-600 hover:text-emerald-700 underline"
                                disabled={otpLoadingLanding}
                                data-testid="resend-otp-btn-landing"
                              >
                                Resend Code
                              </button>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Options Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Membership Options
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {/* Visitor */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="text-center pb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2">Visitor</Badge>
                <CardTitle className="text-2xl">Visitor</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-blue-600">R720</div>
                  <div className="text-sm text-gray-600">Per Round</div>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">✓ League Tournaments Only</li>
                </ul>
              </CardContent>
            </Card>

            {/* Green Member */}
            <Card className="border-2 border-emerald-200">
              <CardHeader className="text-center pb-4">
                <Badge className="bg-emerald-100 text-emerald-800 mb-2">Green</Badge>
                <CardTitle className="text-2xl">Green Member</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-emerald-600">R170</div>
                  <div className="text-sm text-gray-600">Monthly</div>
                  <div className="text-2xl font-bold text-emerald-600 mt-2">R1,890</div>
                  <div className="text-sm text-gray-600">Yearly (R157.50 pm)</div>
                  <div className="text-xs text-gray-500 mt-2">Joining Fee: R300</div>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-center">✓ Comp Fee</li>
                </ul>
              </CardContent>
            </Card>

            {/* Silver Member */}
            <Card className="border-2 border-gray-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gray-800 text-white text-xs">Popular</Badge>
              </div>
              <CardHeader className="text-center pb-4">
                <Badge className="bg-gray-100 text-gray-800 mb-2">Silver</Badge>
                <CardTitle className="text-2xl">Silver Member</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-600">R630</div>
                  <div className="text-sm text-gray-600">Monthly</div>
                  <div className="text-2xl font-bold text-gray-600 mt-2">R6,990</div>
                  <div className="text-sm text-gray-600">Yearly (R582.50 pm)</div>
                  <div className="text-xs text-gray-500 mt-2">Joining Fee: R300</div>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-center">✓ 1 Round/month (12/year)</li>
                  <li className="flex items-center">✓ Comp Fee</li>
                </ul>
              </CardContent>
            </Card>

            {/* Gold Member */}
            <Card className="border-2 border-yellow-300">
              <CardHeader className="text-center pb-4">
                <Badge className="bg-yellow-100 text-yellow-800 mb-2">Gold</Badge>
                <CardTitle className="text-2xl">Gold Member</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-yellow-600">R1,110</div>
                  <div className="text-sm text-gray-600">Monthly</div>
                  <div className="text-2xl font-bold text-yellow-600 mt-2">R12,320</div>
                  <div className="text-sm text-gray-600">Yearly (R1026.67 pm)</div>
                  <div className="text-xs text-gray-500 mt-2">Joining Fee: R300</div>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-center">✓ 2 Rounds/month (24/year)</li>
                  <li className="flex items-center">✓ Comp Fee</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* 2026 Membership Benefits - New Advertisement Bubble */}
          <div className="mt-12">
            <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 shadow-lg">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <img 
                      src="/srixon-black-logo.png" 
                      alt="Srixon"
                      className="h-10"
                      onError={(e) => {
                        e.target.src = "/srixon-red-logo.jpg";
                      }}
                    />
                    <h3 className="text-3xl font-bold text-gray-900">2026 MEMBERSHIPS BENEFITS</h3>
                  </div>
                  <Badge className="bg-red-600 text-white text-lg px-4 py-1">#GolfAmplified</Badge>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8 mt-8">
                  {/* 40+ Tournaments */}
                  <div className="space-y-3">
                    <h4 className="text-xl font-bold text-red-600 mb-4">40+ TOURNAMENTS</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span>33 LEAGUE EVENTS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span>4 MAJORS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span>54 HOLE, CLASSIC TOUR</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span>TEAM SERIES</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span>MATCH PLAY COMPETITIONS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-600 mr-2">✓</span>
                        <span className="font-semibold">34 COURSES IN & AROUND GAUTENG</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Prizes */}
                  <div className="space-y-3">
                    <h4 className="text-xl font-bold text-orange-600 mb-4">PRIZES</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🏆</span>
                        <span>SRIXON BALLS, CAPS, TOWELS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🎯</span>
                        <span>CLOSEST TO PINS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🏆</span>
                        <span className="font-semibold">PLAYER OF THE SEASON AVERAGE: SRIXON IRONS & BAG</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🏆</span>
                        <span className="font-semibold">PLAYER OF SEASON TOTAL: SRIXON HAMPER</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🏆</span>
                        <span>LEAGUE CHAMPIONS: YEAR SUPPLY OF BALLS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">💰</span>
                        <span className="font-semibold">DUCKHOOK CREDITS: EARN 100 TO 3000 CR TO USE TO PAY FOR GOLF</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">✈️</span>
                        <span className="font-semibold">FREE TRIP TO CLASSIC'26</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-orange-600 mr-2">🎲</span>
                        <span className="font-semibold">MONTHLY FREE ROUND DRAW</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Social-Competitive Vibe */}
                  <div className="space-y-3">
                    <h4 className="text-xl font-bold text-emerald-600 mb-4">SOCIAL-COMPETITIVE VIBE</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">📊</span>
                        <span>LIVE SCORING SCORECAPTURE</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">📺</span>
                        <span>LIVE STREAMING GOLF SHOWS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">📈</span>
                        <span>IN-DEPTH TOURNAMENT RESULTS</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">📱</span>
                        <span>ELITE SOCIAL MEDIA CONTENT</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-emerald-600 mr-2">🤝</span>
                        <span className="font-semibold">AMAZING GOLF COMMUNITY</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Ready to Join?
                </h3>
                <p className="text-gray-600 mb-4">
                  To sign up or for more info contact{' '}
                  <a 
                    href="mailto:membership@duckhookgolfers.co.za"
                    className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                  >
                    membership@duckhookgolfers.co.za
                  </a>
                  {' '}or visit{' '}
                  <a 
                    href={`${process.env.REACT_APP_MAIN_SITE_URL || 'https://duckhookgolfers.co.za'}/loyalty-membership/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                  >
                    {process.env.REACT_APP_MAIN_SITE_URL || 'https://duckhookgolfers.co.za'}/loyalty-membership/
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgot-email" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your email address"
                data-testid="forgot-password-email-input"
              />
            </div>
            
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setError('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Login Component - Standalone page (keeping for direct URL access)
const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totpToken: ''
  });
  const [showMFA, setShowMFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // OTP login states
  const [useOTP, setUseOTP] = useState(false);
  const [otpStep, setOtpStep] = useState('email'); // 'email' or 'verify'
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  
  // Remember me functionality
  const [rememberMe, setRememberMe] = useState(false);
  
  // Auto-fill remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('userEmail');
    const shouldRemember = localStorage.getItem('rememberMe') === 'true';
    if (rememberedEmail && shouldRemember) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setOtpEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setError('');

    try {
      await axios.post('/auth/forgot-password', {
        email: forgotPasswordEmail
      });
      
      toast({
        title: "Password Reset Email Sent",
        description: "If the email exists, a password reset link has been sent. Please check your email."
      });
      
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset email';
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/auth/login', {
        username: formData.email, // Use email as username
        password: formData.password,
        totp_token: formData.totpToken || undefined,
        remember_me: rememberMe
      });

      if (response.data.mfa_required) {
        setShowMFA(true);
        toast({
          title: "MFA Required",
          description: "Please enter your authenticator code to continue.",
        });
      } else {
        login(response.data.access_token, response.data.user, rememberMe);
        toast({
          title: "Login Successful",
          description: "Welcome back to Duckhook Golfers!",
        });
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setError('');

    try {
      await axios.post('/auth/request-otp', {
        email: otpEmail
      });
      
      setOtpStep('verify');
      toast({
        title: "OTP Sent",
        description: "A 6-digit code has been sent to your email address.",
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setError('');

    try {
      const response = await axios.post('/auth/verify-otp', {
        email: otpEmail,
        otp_code: otpCode
      });

      login(response.data.access_token, response.data.user, rememberMe);
      toast({
        title: "Login Successful", 
        description: "Welcome back to Duckhook Golfers!",
      });
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOTPComplete = (code) => {
    setOtpCode(code);
  };

  const resetOTPFlow = () => {
    setUseOTP(false);
    setOtpStep('email');
    setOtpEmail('');
    setOtpCode('');
    setError('');
  };

  const handleBiometricLogin = async (email) => {
    try {
      setOtpEmail(email);
      setUseOTP(true);
      setOtpStep('email');
      // Auto-trigger OTP request
      await handleRequestOTP({ preventDefault: () => {} });
    } catch (error) {
      setError('Failed to initiate biometric login. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-gray-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">Welcome Back</CardTitle>
          <p className="text-gray-600 mt-2">Sign in to your Duckhook Golfers account</p>
        </CardHeader>
        <CardContent>
          {/* Login Method Toggle */}
          <div className="mb-6">
            <div className="flex rounded-lg border border-gray-200 p-1">
              <button
                type="button"
                onClick={() => { resetOTPFlow(); setUseOTP(false); }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                  !useOTP 
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="password-login-tab"
              >
                Password Login
              </button>
              <button
                type="button"
                onClick={() => { resetOTPFlow(); setUseOTP(true); }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                  useOTP
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="otp-login-tab"
              >
                Login with OTP
              </button>
            </div>
          </div>

          {/* Regular Password Login */}
          {!useOTP && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="mt-1"
                  placeholder="Enter your email address"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="pr-10"
                    placeholder="Enter your password"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {showMFA && (
                <div>
                  <Label htmlFor="totpToken" className="text-gray-700 font-medium">Authenticator Code</Label>
                  <Input
                    id="totpToken"
                    type="text"
                    value={formData.totpToken}
                    onChange={(e) => setFormData({...formData, totpToken: e.target.value})}
                    required={showMFA}
                    className="mt-1"
                    placeholder="Enter 6-digit code"
                    data-testid="mfa-token-input"
                  />
                </div>
              )}

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <Label htmlFor="remember-me" className="ml-2 text-sm text-gray-700">
                  Remember me (enables biometric login)
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>

              {/* Biometric Authentication */}
              <BiometricAuth 
                onBiometricLogin={handleBiometricLogin}
                isLoading={otpLoading}
              />

              {/* PWA Install Button now in header */}

              <div className="text-center space-y-2">
                <button 
                  type="button"
                  className="text-emerald-600 hover:text-emerald-700 text-sm underline"
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-btn"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          )}

          {/* OTP Login */}
          {useOTP && (
            <div className="space-y-4">
              {otpStep === 'email' && (
                <form onSubmit={handleRequestOTP} className="space-y-4">
                  <div>
                    <Label htmlFor="otp-email" className="text-gray-700 font-medium">Email Address</Label>
                    <Input
                      id="otp-email"
                      type="email"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="Enter your email address"
                      data-testid="otp-email-input"
                    />
                  </div>

                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                    disabled={otpLoading}
                    data-testid="request-otp-btn"
                  >
                    {otpLoading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </form>
              )}

              {otpStep === 'verify' && (
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600">
                      Enter the 6-digit code sent to <span className="font-semibold">{otpEmail}</span>
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium">Verification Code</Label>
                    <div className="mt-2">
                      <OTPInput
                        length={6}
                        onComplete={handleOTPComplete}
                        disabled={otpLoading}
                        className="justify-center"
                      />
                    </div>
                  </div>

                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-3">
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
                      disabled={otpLoading || otpCode.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {otpLoading ? 'Verifying...' : 'Verify & Login'}
                    </Button>

                    <div className="flex justify-center space-x-4 text-sm">
                      <button
                        type="button"
                        onClick={() => setOtpStep('email')}
                        className="text-gray-600 hover:text-gray-800 underline"
                        data-testid="back-to-email-btn"
                      >
                        Change Email
                      </button>
                      <button
                        type="button"
                        onClick={handleRequestOTP}
                        className="text-emerald-600 hover:text-emerald-700 underline"
                        disabled={otpLoading}
                        data-testid="resend-otp-btn"
                      >
                        Resend Code
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button 
                onClick={() => navigate('/register')}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                data-testid="goto-register-btn"
              >
                Sign up
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgot-email-login" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="forgot-email-login"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your email address"
                data-testid="forgot-password-email-login-input"
              />
            </div>
            
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setError('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Register Component - Updated according to requirements
const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    phone: '',
    saga_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  const validatePhone = (phone) => {
    // Must be exactly 11 digits and start with "27"
    const phoneRegex = /^27\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateSagaNumber = (sagaNumber) => {
    // Must start with "27" and be exactly 10 digits
    const sagaRegex = /^27\d{8}$/;
    return sagaRegex.test(sagaNumber);
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!validatePhone(formData.phone)) {
      setError('Phone number must be 11 digits starting with 27 (e.g., 27826943229)');
      setLoading(false);
      return;
    }

    if (!validateSagaNumber(formData.saga_number)) {
      setError('SAGA number must be 10 digits starting with 27');
      setLoading(false);
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await axios.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirm_password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        saga_number: formData.saga_number
      });
      
      toast({
        title: "Registration Successful",
        description: "Your account has been created successfully! Please log in.",
      });
      navigate('/login');
    } catch (error) {
      setError(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-gray-50 flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">Join Duckhook Golfers</CardTitle>
          <p className="text-gray-600 mt-2">Create your account to start booking</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name" className="text-gray-700 font-medium">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="register-firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-700 font-medium">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                  className="mt-1"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                className="mt-1"
                placeholder="27826943229"
                data-testid="register-phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">11 digits starting with 27</p>
            </div>

            <div>
              <Label htmlFor="saga_number" className="text-gray-700 font-medium">SAGA Number</Label>
              <Input
                id="saga_number"
                type="text"
                value={formData.saga_number}
                onChange={(e) => setFormData({...formData, saga_number: e.target.value})}
                required
                className="mt-1"
                placeholder="27xxxxxxxx"
                data-testid="register-saga-input"
              />
              <p className="text-xs text-gray-500 mt-1">10 digits starting with 27</p>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="pr-10"
                  placeholder="Min 8 chars, upper/lower/numbers/special"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 8 characters with uppercase, lowercase, number, and special character</p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-gray-700 font-medium">Confirm Password</Label>
              <div className="relative mt-1">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  required
                  className="pr-10"
                  placeholder="Re-enter your password"
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                data-testid="goto-login-btn"
              >
                Sign in
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component - Updated with new info display
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userBookedEventIds, setUserBookedEventIds] = useState(new Set()); // Track events user has booked
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('upcoming'); // 'upcoming', 'past', 'all'

  // Refresh events when component mounts or when user navigates back to dashboard
  useEffect(() => {
    fetchEvents();
    fetchUserBookings();
  }, []); // Keep initial mount

  // Add effect to refresh events when the page becomes visible
  useEffect(() => {
    const handleFocus = () => {
      console.log('Dashboard focused - refreshing events');
      fetchEvents();
      fetchUserBookings();
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('Dashboard visible - refreshing events');
        fetchEvents();
        fetchUserBookings();
      }
    });
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  const fetchEvents = async () => {
    try {
      console.log('Fetching events for dashboard...');
      const response = await axios.get('/events');
      console.log('Dashboard events fetched:', response.data.length);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBookings = async () => {
    try {
      const response = await axios.get('/bookings');
      // Extract unique event IDs where:
      // 1. User has CONFIRMED bookings only (not pending/cancelled)
      // 2. The booking is FOR the user themselves (is_own_booking = true)
      // Excludes: bookings user made for others, guest bookings, reserved slots
      const bookedEventIds = new Set(
        response.data
          .filter(b => b.status === 'confirmed' && b.is_own_booking === true)
          .map(b => b.event_id)
      );
      setUserBookedEventIds(bookedEventIds);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-welcome">
              Welcome back, {user?.first_name || 'Golfer'}!
            </h1>
            <p className="text-gray-600 mt-2">Ready for your next round?</p>
          </div>
          {/* WhatsApp group quick-link — Dashboard only, same URL as footer link */}
          <a
            href="https://chat.whatsapp.com/IvRyxi6vGVW6T7OR2GkjCl?mode=hqrt3"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Join the Duckhook Info WhatsApp group"
            title="Join our Info WhatsApp group"
            data-testid="dashboard-whatsapp-link"
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe57] transition-colors shadow-md"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              className="w-6 h-6 sm:w-7 sm:h-7 text-white"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.27 1.964.776 2.834 1.245 2.105 2.853 3.99 4.987 5.218.688.4 1.418.73 2.18.96.328.092.658.166.99.166.832 0 1.795-.66 2.005-1.49.115-.515.115-1.118-.058-1.418-.143-.244-.385-.387-.7-.5-.232-.094-.488-.244-.658-.272z"/>
              <path d="M16.04 0C7.32 0 .26 7.06.26 15.78c0 2.795.74 5.49 2.13 7.892l-2.38 7.07 7.39-2.36c2.31 1.243 4.892 1.89 7.49 1.89h.013c8.715 0 15.78-7.06 15.78-15.78A15.69 15.69 0 0 0 26.66 4.62 15.62 15.62 0 0 0 16.04 0zm0 28.93h-.01a13.1 13.1 0 0 1-6.685-1.83l-.48-.286-4.99 1.595 1.61-4.866-.31-.498a13.07 13.07 0 0 1-2.005-6.994c0-7.227 5.886-13.113 13.115-13.113 3.503 0 6.792 1.365 9.27 3.842a13.05 13.05 0 0 1 3.84 9.27c0 7.227-5.885 13.114-13.115 13.114z"/>
            </svg>
          </a>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-r from-red-500 to-orange-600 text-white border-0">
            <CardContent className="p-6">
              <h3 className="text-sm font-medium opacity-90">Membership Status</h3>
              <p className="text-2xl font-bold mt-2 capitalize">{user?.membership_tier || 'Non-Member'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">
            <CardContent className="p-6">
              <h3 className="text-sm font-medium opacity-90">Credits Balance</h3>
              <p className="text-2xl font-bold mt-2">R{user?.credits_balance?.toFixed(2) || '0.00'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0">
            <CardContent className="p-6">
              <h3 className="text-sm font-medium opacity-90">Free Rounds</h3>
              <p className="text-2xl font-bold mt-2">{user?.free_rounds || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Available Events */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Events</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {/* Search Input */}
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                  data-testid="event-search-input"
                />
              </div>
              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="event-date-filter">
                  <SelectValue placeholder="Filter by date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="past">Past Events</SelectItem>
                  <SelectItem value="all">All Events</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setLoading(true);
                  fetchEvents();
                }}
                className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
              >
                Refresh
              </Button>
            </div>
          </div>

          {events.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {events
                  .filter((event) => {
                    // Search filter
                    const matchesSearch = !searchTerm || 
                      event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()));
                    
                    // Date filter
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const eventDate = new Date(event.date);
                    const isPast = eventDate < today;
                    
                    let matchesDate = true;
                    if (dateFilter === 'upcoming') {
                      matchesDate = !isPast;
                    } else if (dateFilter === 'past') {
                      matchesDate = isPast;
                    }
                    // 'all' shows everything
                    
                    return matchesSearch && matchesDate;
                  })
                  .sort((a, b) => {
                    // Sort upcoming events first, then by date
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const aDate = new Date(a.date);
                    const bDate = new Date(b.date);
                    const aIsPast = aDate < today;
                    const bIsPast = bDate < today;
                    if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;
                    return aDate - bDate;
                  })
                  .map((event) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const eventDate = new Date(event.date);
                    const isPastEvent = eventDate < today;
                    return (
                  <Card key={event.id} className={`hover:shadow-lg transition-shadow duration-200 ${isPastEvent ? 'opacity-60' : ''}`}>
                    <CardContent className="p-0">
                      {/* Event Image */}
                      <div className="relative h-48 w-full overflow-hidden rounded-t-lg bg-gradient-to-br from-emerald-100 to-green-100">
                        {event.image_url ? (
                          <img
                            src={event.image_url}
                            alt={event.name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              // Show placeholder instead of hiding
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="h-full w-full flex items-center justify-center text-emerald-600">
                                  <div class="text-center">
                                    <svg class="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                      <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
                                    </svg>
                                    <p class="text-sm font-medium">Golf Event</p>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-emerald-600">
                            <div className="text-center">
                              <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                              </svg>
                              <p className="text-sm font-medium">Golf Event</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-6">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-gray-900">{event.name}</h3>
                          <div className="flex flex-col gap-1 items-end">
                            {/* Already Booked indicator */}
                            {userBookedEventIds.has(event.id) && (
                              <Badge className="bg-blue-600 text-white" data-testid={`already-booked-${event.id}`}>
                                ✓ Already Booked
                              </Badge>
                            )}
                            {isPastEvent ? (
                              <Badge className="bg-gray-500 text-white">
                                Event Ended
                              </Badge>
                            ) : (
                              <>
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  {event.available_slots} slots
                                </Badge>
                                {!event.bookings_open && (
                                  <Badge className="bg-red-100 text-red-800">
                                    Closed
                                  </Badge>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {event.description && (
                          <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                        )}
                        
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <p>📅 {new Date(event.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric', 
                            month: 'long',
                            day: 'numeric'
                          })}</p>
                          <p>⏰ {event.start_time} - {event.end_time}</p>
                          <p>💰 Members: R{event.member_fee} • Non-members: R{event.non_member_fee}</p>
                          {event.cart_fee > 0 && (
                            <p>🏌️ Cart Fee: R{event.cart_fee} {event.cart_required ? '(Required)' : '(Optional)'}</p>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            className={`w-full ${
                              isPastEvent
                                ? 'bg-gray-500 hover:bg-gray-600 text-white'
                                : event.available_slots === 0 
                                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed hover:bg-gray-400' 
                                  : (!event.bookings_open 
                                    ? 'bg-yellow-600 hover:bg-yellow-700' 
                                    : event.available_slots === 0
                                      ? 'bg-gray-500 hover:bg-gray-600'
                                      : 'bg-emerald-600 hover:bg-emerald-700')
                            }`}
                            onClick={() => navigate(`/tee-sheet/${event.id}`)}
                            data-testid={`book-event-${event.id}`}
                          >
                            {isPastEvent ? 'View Tee Sheet (Past Event)' :
                             !event.bookings_open ? 'View Tee Sheet (Closed)' : 
                             event.available_slots === 0 ? 'View Tee Sheet (Full)' : 
                             'Book Tee Time'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>

              <div className="text-center">
                <Button 
                  onClick={() => navigate('/events')}
                  variant="outline"
                  className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                  data-testid="more-events-btn"
                >
                  More Events →
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Available</h3>
                <p className="text-gray-600">Check back later for upcoming golf events.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Profile Page Component with MFA Setup - Updated with new features
const ProfilePage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);

  // Refresh user data when ProfilePage mounts to get latest admin updates
  useEffect(() => {
    refreshUser();
  }, []);

  // Credit top-up state
  const [topUpAmount, setTopUpAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleTopUp = (amount) => {
    const finalAmount = amount === 'custom' ? parseFloat(customAmount) : amount;
    
    if (finalAmount < 100) {
      toast({
        variant: "destructive",
        title: "Invalid Amount",
        description: "Minimum top-up amount is R100"
      });
      return;
    }

    if (finalAmount > 10000) {
      toast({
        variant: "destructive", 
        title: "Invalid Amount",
        description: "Maximum top-up amount is R10,000"
      });
      return;
    }

    // Set the amount and show payment modal
    setTopUpAmount(finalAmount);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    toast({
      title: "Payment Successful",
      description: "Your credits have been updated"
    });
    // Refresh user data to show updated balance
    refreshUser();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard-btn"
          >
            ← Back to Dashboard
          </Button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
            <p className="text-gray-600 mt-2">Manage your account information and preferences</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProfileSettings />
          
          {/* DH Information & Credits on the right side */}
          <div className="space-y-6">
            {/* DH Information */}
            <Card>
              <CardHeader>
                <CardTitle>DH Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">DH Number</Label>
                    <p className="text-lg font-semibold text-gray-900">
                      {user?.dh_number || 'Not assigned'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">DH Handicap</Label>
                    <p className="text-lg font-semibold text-emerald-600">
                      {user?.dh_handicap ? 
                        (user.role === 'admin' ? 
                          parseFloat(user.dh_handicap).toFixed(1) : 
                          Math.round(parseFloat(user.dh_handicap))
                        ) : 'Not set'
                      }
                    </p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-500">DH 95%</Label>
                  <p className="text-lg font-semibold text-blue-600">
                    {user?.dh_95_percent !== null && user?.dh_95_percent !== undefined ? 
                      user.dh_95_percent : 
                      (user?.dh_handicap ? 'Calculating...' : 'Not set')
                    }
                  </p>
                  {user?.dh_handicap && (
                    <p className="text-xs text-gray-500 mt-1">
                      Calculated by system: DH Handicap × 0.95
                    </p>
                  )}
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 italic">
                    DH information can only be updated by administrators
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Credits & Free Rounds */}
            <Card>
              <CardHeader>
                <CardTitle>Credits & Free Rounds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-600 mb-1">
                      R{user?.credits_balance?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-sm text-emerald-700">Available Credits</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {user?.free_rounds || 0}
                    </div>
                    <div className="text-sm text-blue-700">Free Rounds</div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Top Up Credits (Card Payment)
                  </Label>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <Button 
                      variant="outline"
                      onClick={() => {setTopUpAmount(100); setShowCustomAmount(false); setShowPaymentModal(true);}}
                      className="text-sm"
                    >
                      R100
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {setTopUpAmount(200); setShowCustomAmount(false); setShowPaymentModal(true);}}
                      className="text-sm"
                    >
                      R200
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {setTopUpAmount(500); setShowCustomAmount(false); setShowPaymentModal(true);}}
                      className="text-sm"
                    >
                      R500
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <Input
                      type="number"
                      placeholder="Custom amount"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setShowCustomAmount(true);
                      }}
                      className="text-center"
                    />
                    
                    <Button 
                      onClick={() => setShowPaymentModal(true)}
                      disabled={!showCustomAmount || !customAmount || parseFloat(customAmount) <= 0}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      Top Up {showCustomAmount && customAmount ? `R${parseFloat(customAmount).toFixed(2)}` : ''}
                    </Button>
                  </div>
              </div>
            </CardContent>
          </Card>

          </div>
        </div>

        {/* PayFast Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={showCustomAmount ? parseFloat(customAmount) || topUpAmount : topUpAmount}
          description="Credit Top-up"
          onPaymentSuccess={handlePaymentSuccess}
          paymentType="credit-topup"
        />
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshUser, isAuthenticated, login, user } = useAuth();
  const { clearCart } = useCart();  // Add cart context to clear cart after payment
  const [processed, setProcessed] = React.useState(false);
  const [restoringSession, setRestoringSession] = React.useState(false);
  const [sessionRestored, setSessionRestored] = React.useState(false);
  const [sessionRestorationAttempted, setSessionRestorationAttempted] = React.useState(false);
  
  React.useEffect(() => {
    // Use a more persistent flag to prevent multiple executions (works with React.StrictMode)
    const sessionKey = 'session_restoration_attempted_' + window.location.search;
    if (sessionStorage.getItem(sessionKey) === 'true') {
      console.log('🚫 Session restoration already attempted, skipping');
      return;
    }

    const handleSessionRestoration = async () => {
      // Mark restoration as attempted immediately using sessionStorage to prevent re-runs
      sessionStorage.setItem(sessionKey, 'true');
      setSessionRestorationAttempted(true);
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const wasProcessed = urlParams.get('processed') === 'true';
        const needsProcessing = urlParams.get('process') === 'true';
        const paymentRef = urlParams.get('ref');
        setProcessed(wasProcessed);
        
        console.log('🔍 PaymentSuccess URL params:', {
          processed: wasProcessed,
          needsProcessing,
          paymentRef,
          fullURL: window.location.href
        });
        
        // If this is a direct return from PayFast, process the payment via backend API
        if (needsProcessing && paymentRef) {
          console.log('📞 Processing PayFast payment via backend API...');
          setRestoringSession(true);
          
          try {
            // Call backend to process the payment and restore session
            const response = await axios.post('/payment/process-payfast-return', {
              payment_ref: paymentRef
            });
            
            console.log('✅ PayFast payment processing response:', response.data);
            
            // Clear the cart after successful payment
            clearCart();
            console.log('🛒 Cart cleared after successful payment');
            
            // If we got session restoration data, use it
            if (response.data.access_token && response.data.user) {
              console.log('🔑 Session restoration data received, logging in...');
              login(response.data.access_token, response.data.user, false);
              setSessionRestored(true);
              
              toast({
                title: "Payment Successful & Logged In!",
                description: "Your payment was processed and you're automatically logged back in.",
              });
            } else {
              // Payment processed but no session restoration
              setProcessed(true);
              toast({
                title: "Payment Successful!",
                description: "Your payment has been processed successfully.",
              });
            }
            
          } catch (processError) {
            console.error('❌ PayFast payment processing error:', processError);
            // Still clear cart since PayFast confirmed the payment
            clearCart();
            // Still show success since PayFast confirmed the payment
            toast({
              title: "Payment Successful!",
              description: "Your payment was completed. Please log in to see your updated account.",
            });
          } finally {
            setRestoringSession(false);
          }
          return;
        }
        
        // Check for session restoration parameters (legacy flow)
        const restoreSession = urlParams.get('restore_session') === 'true';
        const userId = urlParams.get('user_id');
        const userEmail = urlParams.get('user_email');
        
        console.log('🔍 PaymentSuccess Legacy params:', {
          restoreSession,
          hasUserId: !!userId,
          userEmail,
          isAuthenticated
        });
        
        // Only proceed with restoration if user is not authenticated and parameters exist
        if (restoreSession && userId && !isAuthenticated && !sessionRestored) {
          setRestoringSession(true);
          
          try {
            console.log('🔄 PayFast session restoration starting for:', userEmail);
            console.log('🔑 User ID received:', userId ? 'Yes' : 'No');
            
            // Clear any existing auth state first
            localStorage.removeItem('auth_token');
            
            // Store the user ID for session restoration
            localStorage.setItem('restore_user_id', userId);
            
            // Note: No direct token available, will need to authenticate via user ID
            console.log('🔧 Stored user ID for session restoration');
            
            // Wait a moment for setup to settle
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Fetch user data using user ID for session restoration
            console.log('📞 Attempting session restoration with user ID');
            
            try {
              // Make API call to restore session using user ID
              const response = await axios.post('/auth/restore-session', { user_id: userId });
              console.log('✅ Session restoration successful, user data received:', response.data);
              
              // Use login function to properly set user state with the returned token
              login(response.data.access_token, response.data.user, false);
              
              // Mark session as restored to prevent future attempts
              setSessionRestored(true);
              
            } catch (authError) {
              console.error('❌ Session restoration failed:', authError);
              console.error('❌ Auth error details:', authError.response?.data);
              throw authError; // This will be caught by outer try-catch
            }
            
            console.log('✅ PayFast session restoration completed successfully');
            
            // Clear the cart after successful payment
            clearCart();
            console.log('🛒 Cart cleared after successful payment (legacy flow)');
            
            toast({
              title: "Payment Successful & Logged In!",
              description: "Your payment was processed and you're automatically logged back in.",
            });
            
          } catch (error) {
            console.error('❌ PayFast session restoration failed:', error);
            
            // Still clear cart since PayFast confirmed the payment
            clearCart();
            
            // Clear failed restoration data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('restore_user_id');
            
            toast({
              title: "Payment Successful!",
              description: "Your payment completed successfully. Please log in to see your updated account.",
            });
          } finally {
            setRestoringSession(false);
          }
        } else {
          // Normal success message (only if not already showing restoration message)
          if (!sessionRestored && !restoringSession) {
            // Clear cart for any successful payment
            clearCart();
            console.log('🛒 Cart cleared (normal success path)');
            
            toast({
              title: "Payment Successful!",
              description: wasProcessed 
                ? "Your payment has been processed and your account updated." 
                : "Your payment was successful. If you don't see updates, please contact support.",
            });
          }
        }
        
        // Clear any pending payment ID from localStorage
        localStorage.removeItem('pending_payment_id');
        
        // If user is authenticated, refresh their data (but only once)
        if (isAuthenticated && !sessionRestored) {
          refreshUser();
        }
        
        // DISABLE ALL AUTOMATIC REDIRECTS - Let user stay on success page
        console.log('🚫 All automatic redirects disabled - user stays on payment success page');
        
      } catch (error) {
        console.error('❌ Error in payment success handling:', error);
        
        // Show success message but DON'T redirect to login
        toast({
          title: "Payment Successful!",
          description: "Your payment was completed successfully. Use the navigation menu to continue.",
        });
        
        // NO automatic redirect to login - let user navigate manually
        console.log('💡 Payment successful but session restoration failed - user can navigate manually');
      }
    };
    
    handleSessionRestoration();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-md text-center">
        <CardContent className="p-8">
          {restoringSession ? (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin text-2xl">🔄</div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Restoring Your Session...</h1>
              <p className="text-gray-600 mb-6">
                Your payment was successful! We're logging you back in automatically.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful! 🎉</h1>
              <p className="text-gray-600 mb-6">
                {processed 
                  ? "Your payment has been processed successfully. Your account has been updated."
                  : "Your payment was completed successfully. You will be redirected automatically."}
              </p>
              {isAuthenticated && (
                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/profile')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    View Profile
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="w-full"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              )}
            </>
          )}
          
          {!restoringSession && (
            <div className="mt-4">
              {isAuthenticated ? (
                <div className="text-sm text-gray-500">
                  Redirecting to your profile in a few seconds...
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    Your payment was successful! Use the navigation menu to continue.
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/bookings')}
                      className="flex-1"
                    >
                      My Bookings
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/events')}
                      className="flex-1"
                    >
                      Browse Events
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Cancelled Page
const PaymentCancelledPage = () => {
  const navigate = useNavigate();
  const [processing, setProcessing] = React.useState(true);
  const [processed, setProcessed] = React.useState(false);
  
  React.useEffect(() => {
    const processCancellation = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentRef = urlParams.get('ref');
        
        console.log('🚫 Processing cancelled payment:', paymentRef);
        
        if (paymentRef) {
          // Call backend to clean up the pending booking
          const response = await axios.post('/payment/process-payfast-cancelled', {
            payment_ref: paymentRef
          });
          console.log('✅ Cancellation processed:', response.data);
          setProcessed(true);
        }
      } catch (error) {
        console.error('❌ Error processing cancellation:', error);
      } finally {
        setProcessing(false);
      }
    };
    
    processCancellation();
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-md text-center">
        <CardContent className="p-8">
          {processing ? (
            <>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="animate-spin text-2xl">⏳</div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h1>
              <p className="text-gray-600 mb-6">
                Cleaning up your cancelled transaction...
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h1>
              <p className="text-gray-600 mb-6">
                Your payment was cancelled. No charges have been made and no booking was created.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/events')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  Browse Events
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Email Verification Page
const EmailVerificationPage = () => {
  const [verifying, setVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setError('No verification token provided');
      setVerifying(false);
      return;
    }
    
    verifyEmailToken();
  }, [token]);
  
  const verifyEmailToken = async () => {
    try {
      const response = await axios.get(`/verify-email/${token}`);
      setVerificationStatus({
        success: true,
        message: response.data.message,
        email: response.data.email
      });
      
      toast({
        title: "Email Verified!",
        description: "Your email has been successfully verified. You can now log in.",
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Verification failed');
      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: error.response?.data?.detail || "Invalid or expired verification token",
      });
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email...</h1>
            <p className="text-gray-600">Please wait while we verify your email address.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/register')}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Register Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-md text-center">
        <CardContent className="p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
          <p className="text-gray-600 mb-2">
            Your email address has been successfully verified.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Email: {verificationStatus?.email}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Continue to Login
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Redirecting to login in 3 seconds...
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Profile Settings Component
const ProfileSettings = () => {
  const { user, toast, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    saga_number: ''
  });

  // Refresh user data when component mounts to get latest updates
  useEffect(() => {
    refreshUser();
  }, []);

  // Initialize form when user data loads
  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        saga_number: user.saga_number || ''
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      await axios.put('/auth/profile', profileForm);
      
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully"
      });
      
      setIsEditing(false);
      // The user data will be refreshed by the auth context
      window.location.reload(); // Simple refresh to update user context
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to update profile"
      });
    }
  };

  const handleCancelEdit = () => {
    // Reset form to original user data
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      email: user?.email || '',
      saga_number: user?.saga_number || ''
    });
    setIsEditing(false);
  };

  const membershipStyles = {
    'non-member': { className: 'bg-gray-100 text-gray-800', label: 'Non-Member' },
    'green': { className: 'bg-green-100 text-green-800', label: 'Green' },
    'silver': { className: 'bg-gray-100 text-gray-800', label: 'Silver' },
    'gold': { className: 'bg-yellow-100 text-yellow-800', label: 'Gold' }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <Card>
        <CardHeader className="text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-emerald-600">
              {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
            </span>
          </div>
          <CardTitle>{user?.first_name} {user?.last_name}</CardTitle>
          <p className="text-gray-500 text-sm">{user?.email}</p>
          <Badge 
            className={membershipStyles[user?.membership_tier]?.className || 'bg-gray-100 text-gray-800'}
          >
            {membershipStyles[user?.membership_tier]?.label || 'Non-Member'}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isEditing ? (
            // View Mode
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">First Name</Label>
                  <p className="text-gray-900">{user?.first_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Last Name</Label>
                  <p className="text-gray-900">{user?.last_name}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Email</Label>
                <p className="text-gray-900">{user?.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Phone</Label>
                <p className="text-gray-900">{user?.phone}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">SAGA Number</Label>
                <p className="text-gray-900">{user?.saga_number}</p>
              </div>
              
              <div className="pt-4 border-t">
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="w-full"
                >
                  Edit Profile
                </Button>
              </div>
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  placeholder="27xxxxxxxxx"
                />
              </div>
              
              <div>
                <Label htmlFor="saga_number">SAGA Number</Label>
                <Input
                  id="saga_number"
                  value={profileForm.saga_number}
                  onChange={(e) => setProfileForm({...profileForm, saga_number: e.target.value})}
                  placeholder="27xxxxxxxx"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveProfile} className="flex-1">
                  Save Profile
                </Button>
                <Button onClick={handleCancelEdit} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Status */}
      <Card>
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-500">Email Status</Label>
              <div className="flex items-center mt-1">
                {user?.email_verified ? (
                  <Badge className="bg-green-100 text-green-800">Verified</Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800">Unverified</Badge>
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-500">Account Type</Label>
              <p className="text-gray-900 capitalize">{user?.role || 'User'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Events Page Component
const EventsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userBookedEventIds, setUserBookedEventIds] = useState(new Set());

  useEffect(() => {
    fetchEvents();
    fetchUserBookings();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load events"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBookings = async () => {
    try {
      const response = await axios.get('/bookings');
      // Extract unique event IDs where:
      // 1. User has CONFIRMED bookings only (not pending/cancelled)
      // 2. The booking is FOR the user themselves (is_own_booking = true)
      // Excludes: bookings user made for others, guest bookings, reserved slots
      const bookedEventIds = new Set(
        response.data
          .filter(b => b.status === 'confirmed' && b.is_own_booking === true)
          .map(b => b.event_id)
      );
      setUserBookedEventIds(bookedEventIds);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard-btn"
          >
            ← Back to Dashboard
          </Button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">All Events</h1>
            <p className="text-gray-600 mt-2">Browse and book all available golf events</p>
          </div>
        </div>

        {events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events
              .sort((a, b) => {
                // Sort upcoming events first, then by date
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const aDate = new Date(a.date);
                const bDate = new Date(b.date);
                const aIsPast = aDate < today;
                const bIsPast = bDate < today;
                if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;
                return aDate - bDate;
              })
              .map((event) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const eventDate = new Date(event.date);
                const isPastEvent = eventDate < today;
                return (
              <Card key={event.id} className={`hover:shadow-lg transition-shadow duration-200 ${isPastEvent ? 'opacity-60' : ''}`}>
                <CardContent className="p-0">
                  {/* Event Image */}
                  <div className="relative h-48 w-full overflow-hidden rounded-t-lg bg-gradient-to-br from-emerald-100 to-green-100">
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          // Show placeholder instead of hiding
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="h-full w-full flex items-center justify-center text-emerald-600">
                              <div class="text-center">
                                <svg class="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
                                </svg>
                                <p class="text-sm font-medium">Golf Event</p>
                              </div>
                            </div>
                          `;
                        }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-emerald-600">
                        <div className="text-center">
                          <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                          </svg>
                          <p className="text-sm font-medium">Golf Event</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-900">{event.name}</h3>
                      <div className="flex flex-col gap-1 items-end">
                        {/* Already Booked indicator */}
                        {userBookedEventIds.has(event.id) && (
                          <Badge className="bg-blue-600 text-white" data-testid={`already-booked-events-${event.id}`}>
                            ✓ Already Booked
                          </Badge>
                        )}
                        {isPastEvent ? (
                          <Badge className="bg-gray-500 text-white">
                            Event Ended
                          </Badge>
                        ) : (
                          <>
                            <Badge className="bg-emerald-100 text-emerald-800">
                              {event.available_slots} slots
                            </Badge>
                            {!event.bookings_open && (
                              <Badge className="bg-red-100 text-red-800">
                                Closed
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {event.description && (
                      <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                    )}
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p>📅 {new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long',
                      day: 'numeric'
                    })}</p>
                    <p>⏰ {event.start_time} - {event.end_time}</p>
                    <p>💰 Members: R{event.member_fee} • Non-members: R{event.non_member_fee}</p>
                    {event.cart_fee > 0 && (
                      <p>🏌️ Cart Fee: R{event.cart_fee} {event.cart_required ? '(Required)' : '(Optional)'}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      className={`w-full ${
                        isPastEvent
                          ? 'bg-gray-500 hover:bg-gray-600 text-white'
                          : event.available_slots === 0 
                            ? 'bg-gray-500 hover:bg-gray-600' 
                            : (!event.bookings_open 
                              ? 'bg-yellow-600 hover:bg-yellow-700' 
                              : 'bg-emerald-600 hover:bg-emerald-700')
                      }`}
                      onClick={() => navigate(`/tee-sheet/${event.id}`)}
                      data-testid={`book-event-${event.id}`}
                    >
                      {isPastEvent ? 'View Tee Sheet (Past Event)' :
                       !event.bookings_open ? 'View Tee Sheet (Closed)' : 
                       event.available_slots === 0 ? 'View Tee Sheet (Full)' : 
                       'Book Tee Time'}
                    </Button>
                  </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Available</h3>
              <p className="text-gray-600">Check back later for upcoming golf events.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Main App Component
// Reset Password Page Component
const ResetPasswordPage = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get token from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  useEffect(() => {
    if (!token) {
      toast({
        title: "Invalid Reset Link",
        description: "The password reset link is invalid or expired."
      });
      navigate('/login');
    }
  }, [token, navigate, toast]);

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await axios.post('/auth/reset-password', {
        token: token,
        new_password: formData.password,
        confirm_password: formData.confirm_password
      });
      
      setSuccess(true);
      toast({
        title: "Password Reset Successful",
        description: "Your password has been reset successfully. You can now log in with your new password."
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-emerald-600">Password Reset Successful!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="text-6xl mb-4">✅</div>
            <p className="text-gray-600">
              Your password has been reset successfully. You will be redirected to the login page shortly.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Reset Your Password</CardTitle>
          <p className="text-center text-gray-600">Enter your new password below</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="pr-10"
                  placeholder="Min 8 chars, upper/lower/numbers/special"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 8 characters with uppercase, lowercase, number, and special character</p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-gray-700 font-medium">Confirm New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  required
                  className="pr-10"
                  placeholder="Re-enter your new password"
                  data-testid="reset-confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2"
              disabled={loading}
              data-testid="reset-password-submit-btn"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <div className="text-center">
              <button 
                type="button"
                onClick={() => navigate('/login')}
                className="text-emerald-600 hover:text-emerald-700 text-sm underline"
              >
                Back to Login
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Cart Page Component
const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, removeFromCart, clearCart, getCartTotals } = useCart();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'credits'
  const [useCredits, setUseCredits] = useState(0);
  const [useFreeRounds, setUseFreeRounds] = useState(0);

  const totals = getCartTotals();
  const userCredits = user?.credits_balance || 0;
  const userFreeRounds = user?.free_rounds || 0;

  // Calculate max free rounds usable (limited by user's balance and cart items)
  const maxFreeRoundsUsable = Math.min(userFreeRounds, cartItems.length);
  
  // Calculate discount from free rounds (each covers one item's green fee)
  // Sort items by green fee descending to maximize savings
  const sortedItems = [...cartItems].sort((a, b) => (b.green_fee || 0) - (a.green_fee || 0));
  const freeRoundDiscount = sortedItems
    .slice(0, useFreeRounds)
    .reduce((sum, item) => sum + (item.green_fee || 0), 0);
  
  // Calculate remaining after free rounds
  const totalAfterFreeRounds = totals.grandTotal - freeRoundDiscount;
  
  // Calculate how much can be paid with credits (after free round discount)
  const maxCreditsUsable = Math.min(userCredits, totalAfterFreeRounds);
  const amountAfterCredits = totalAfterFreeRounds - useCredits;

  // Reset credits if free rounds change and credits exceed new max
  useEffect(() => {
    if (useCredits > maxCreditsUsable) {
      setUseCredits(maxCreditsUsable);
    }
  }, [useFreeRounds, maxCreditsUsable, useCredits]);

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast({
        variant: "destructive",
        title: "Cart Empty",
        description: "Please add items to your cart before checkout"
      });
      return;
    }

    setLoading(true);
    try {
      // Prepare cart items for backend
      const checkoutData = {
        items: cartItems.map(item => ({
          event_id: item.event_id,
          tee_time: item.tee_time,
          tee_name: item.tee_name,
          slot_index: item.slot_index,
          players_count: item.players_count || 1,
          cart_required: item.cart_required || false,
          player_names: item.player_names || [],
          mhala_hole_in_one: item.mhala_hole_in_one || false,
          mhala_payment_method: item.mhala_payment_method || 'card',
          dinner_attending: item.dinner_attending || false,
          dinner_partner: item.dinner_partner || false
        })),
        payment_method: paymentMethod,
        use_credits: useCredits,
        use_free_rounds: useFreeRounds
      };

      const response = await axios.post('/cart/checkout', checkoutData);

      if (response.data.payment_form_data) {
        // Redirect to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.payment_form_data.action;
        
        Object.entries(response.data.payment_form_data).forEach(([key, value]) => {
          if (key !== 'action') {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          }
        });
        
        document.body.appendChild(form);
        form.submit();
      } else {
        // No payment needed (all paid with credits/free rounds)
        clearCart();
        toast({
          title: "Bookings Confirmed!",
          description: `${response.data.bookings_created} booking(s) confirmed successfully`
        });
        navigate('/bookings');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        variant: "destructive",
        title: "Checkout Failed",
        description: error.response?.data?.detail || "Failed to process checkout"
      });
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardContent className="p-12 text-center">
              <ShoppingCart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">Your Cart is Empty</h2>
              <p className="text-gray-500 mb-6">Add some tee times to get started</p>
              <Button onClick={() => navigate('/dashboard')} className="bg-emerald-600 hover:bg-emerald-700">
                Browse Events
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Your Cart</h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Continue Shopping
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.cart_item_id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.event_name}</h3>
                      <p className="text-sm text-gray-600">
                        📅 {item.tee_time ? new Date(item.tee_time).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        }) : 'Date TBD'} at {item.tee_time ? new Date(item.tee_time).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Time TBD'}
                      </p>
                      <p className="text-sm text-gray-600">⛳ {item.tee_name} Tee</p>
                      {item.cart_required && (
                        <p className="text-sm text-gray-600">🏌️ Cart included</p>
                      )}
                      {item.mhala_hole_in_one && (
                        <p className="text-sm text-emerald-600">🎯 Mhala Competition (+R{item.mhala_fee || 50})</p>
                      )}
                      {item.dinner_attending && (
                        <p className="text-sm text-purple-600">🍽️ Prize Giving Dinner{item.dinner_partner ? ' + partner' : ''} (+R{(item.dinner_fee || 0).toFixed(2)})</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        R{((item.green_fee || 0) + (item.cart_fee || 0) + (item.mhala_hole_in_one ? (item.mhala_fee || 50) : 0) + (item.dinner_attending ? (item.dinner_fee || 0) : 0)).toFixed(2)}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-2"
                        onClick={() => removeFromCart(item.cart_item_id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outline"
              className="w-full text-red-500 border-red-200 hover:bg-red-50"
              onClick={clearCart}
            >
              Clear Cart
            </Button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Green Fees ({totals.itemCount} items)</span>
                    <span>R{totals.greenFeeTotal.toFixed(2)}</span>
                  </div>
                  {totals.cartFeeTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cart Fees</span>
                      <span>R{totals.cartFeeTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {totals.mhalaFeeTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mhala Competition</span>
                      <span>R{totals.mhalaFeeTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {totals.dinnerFeeTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prize Giving Dinner</span>
                      <span>R{totals.dinnerFeeTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Subtotal</span>
                    <span>R{totals.grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Free Rounds Section */}
                {userFreeRounds > 0 && (
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-purple-800 mb-2">
                      🎟️ Free Rounds Available: {userFreeRounds}
                    </p>
                    <p className="text-xs text-purple-600 mb-2">
                      Each free round covers one booking's green fee
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max={maxFreeRoundsUsable}
                        value={useFreeRounds}
                        onChange={(e) => setUseFreeRounds(Math.min(Math.max(0, Number(e.target.value)), maxFreeRoundsUsable))}
                        className="w-20"
                      />
                      <span className="text-sm text-gray-600">free rounds to use</span>
                    </div>
                    {maxFreeRoundsUsable > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-purple-600 p-0 h-auto mt-1"
                        onClick={() => setUseFreeRounds(maxFreeRoundsUsable)}
                      >
                        Use all ({maxFreeRoundsUsable})
                      </Button>
                    )}
                  </div>
                )}

                {useFreeRounds > 0 && (
                  <div className="flex justify-between text-sm text-purple-600">
                    <span>Free Rounds ({useFreeRounds})</span>
                    <span>-R{freeRoundDiscount.toFixed(2)}</span>
                  </div>
                )}

                {/* Credits Section */}
                {userCredits > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">
                      💰 Available Credits: R{userCredits.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max={maxCreditsUsable}
                        value={useCredits}
                        onChange={(e) => setUseCredits(Math.min(Number(e.target.value), maxCreditsUsable))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">credits to use</span>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-blue-600 p-0 h-auto mt-1"
                      onClick={() => setUseCredits(maxCreditsUsable)}
                    >
                      Use all available
                    </Button>
                  </div>
                )}

                {useCredits > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Credits Applied</span>
                    <span>-R{useCredits.toFixed(2)}</span>
                  </div>
                )}

                <div className="border-t pt-2 flex justify-between text-lg font-bold">
                  <span>Total to Pay</span>
                  <span>R{amountAfterCredits.toFixed(2)}</span>
                </div>

                {amountAfterCredits > 0 ? (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleCheckout}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : `Pay R${amountAfterCredits.toFixed(2)} with Card`}
                  </Button>
                ) : (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleCheckout}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Confirm Bookings (Paid with Credits)'}
                  </Button>
                )}

                <p className="text-xs text-gray-500 text-center">
                  Secure payment via PayFast
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer Component with Auth Context
const AppFooter = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <footer className="bg-gray-50 border-t border-gray-200 py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex flex-col items-center sm:items-start gap-2">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Duckhook Golfers. All rights reserved.
            </p>
            {/* WhatsApp Group Link - Only visible when logged in */}
            {isAuthenticated && (
              <p className="text-xs text-gray-600">
                Use this link to join our info group to keep updated with all information -{' '}
                <a 
                  href="https://chat.whatsapp.com/IvRyxi6vGVW6T7OR2GkjCl?mode=hqrt3" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                >
                  Duckhook Info WhatsApp Group
                </a>
              </p>
            )}
          </div>
          <div className="flex flex-col items-center sm:items-end gap-2">
            {/* Srixon Sponsor */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Proudly sponsored by</span>
              <img 
                src="/srixon-black-logo.png" 
                alt="Srixon"
                className="h-5 opacity-80 hover:opacity-100 transition-opacity"
                onError={(e) => {
                  e.target.src = "/srixon-red-logo.jpg";
                }}
              />
            </div>
            
            {/* ScoreCapture Powered By */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Powered by</span>
              <img 
                src="/scorecapture-logo.png" 
                alt="ScoreCapture"
                className="h-20 opacity-80 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <div className="App min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route 
                  path="/cart" 
                  element={
                    <ProtectedRoute>
                      <CartPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/bookings" 
                  element={
                    <ProtectedRoute>
                      <BookingsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/book/:eventId" 
                  element={
                    <ProtectedRoute>
                      <BookingPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/tee-sheet/:eventId" 
                  element={
                    <ProtectedRoute>
                      <TeeSheetView />
                    </ProtectedRoute>
                  } 
                />
              <Route 
                path="/presentation-tee-sheet/:eventId" 
                element={
                  <ProtectedRoute>
                    <PresentationTeeSheet />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <AdminPanel />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/payment/success" 
                element={<PaymentSuccessPage />}
              />
              <Route 
                path="/payment/cancelled" 
                element={<PaymentCancelledPage />}
              />
              <Route 
                path="/verify-email" 
                element={<EmailVerificationPage />}
              />
              <Route 
                path="/events" 
                element={
                  <ProtectedRoute>
                    <EventsPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
          
          {/* Footer with Sponsor Logo - Visible on All Devices */}
          <AppFooter />
          
          {/* PWA Install Button now in header */}
        </div>
      </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
export { useAuth, useCart };